'use client'

import React, { useState } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { BaseFieldProps } from '../types'
import { cn, baseInputStyles, inputVariants, labelStyles, errorStyles, helperTextStyles, buttonStyles } from '../utils/styles'

export interface DatePickerProps extends BaseFieldProps {
  value?: Date | string | null
  onChange: (date: Date | null) => void
  placeholder?: string
  dateFormat?: 'dd.MM.yyyy' | 'MM/dd/yyyy' | 'yyyy-MM-dd'
  minDate?: Date
  maxDate?: Date
  showWeekNumbers?: boolean
  disabledDates?: Date[]
  disableWeekends?: boolean
}

// Simple calendar implementation
function Calendar({
  selected,
  onSelect,
  minDate,
  maxDate,
  disabledDates = [],
  disableWeekends = false,
  showWeekNumbers = false,
  className
}: {
  selected?: Date | null
  onSelect: (date: Date) => void
  minDate?: Date
  maxDate?: Date
  disabledDates?: Date[]
  disableWeekends?: boolean
  showWeekNumbers?: boolean
  className?: string
}) {
  const [currentMonth, setCurrentMonth] = useState(selected || new Date())

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate()
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay()
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))
  }

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))
  }

  const isDateDisabled = (date: Date) => {
    if (minDate && date < minDate) return true
    if (maxDate && date > maxDate) return true
    if (disableWeekends && (date.getDay() === 0 || date.getDay() === 6)) return true
    if (disabledDates.some(disabledDate =>
      disabledDate.toDateString() === date.toDateString()
    )) return true
    return false
  }

  const renderCalendarDay = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
    const isSelected = selected && date.toDateString() === selected.toDateString()
    const isToday = date.toDateString() === new Date().toDateString()
    const disabled = isDateDisabled(date)

    return (
      <button
        key={day}
        type="button"
        onClick={() => !disabled && onSelect(date)}
        disabled={disabled}
        className={cn(
          'h-9 w-9 text-center text-sm transition-colors hover:bg-gray-100 focus:outline-none focus:bg-gray-100',
          'rounded-md',
          isSelected && 'bg-blue-600 text-white hover:bg-blue-700 focus:bg-blue-700',
          isToday && !isSelected && 'bg-gray-200 font-semibold',
          disabled && 'text-gray-300 cursor-not-allowed hover:bg-transparent'
        )}
      >
        {day}
      </button>
    )
  }

  return (
    <div className={cn('p-3 space-y-3', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={previousMonth}
          className="p-1 hover:bg-gray-100 rounded-md focus:outline-none focus:bg-gray-100"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <h2 className="text-sm font-semibold">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h2>

        <button
          type="button"
          onClick={nextMonth}
          className="p-1 hover:bg-gray-100 rounded-md focus:outline-none focus:bg-gray-100"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Day headers */}
        {dayNames.map((dayName) => (
          <div
            key={dayName}
            className="h-9 flex items-center justify-center text-xs font-medium text-gray-500"
          >
            {dayName}
          </div>
        ))}

        {/* Empty cells for days before month starts */}
        {Array.from({ length: firstDayOfMonth }, (_, i) => (
          <div key={`empty-${i}`} className="h-9" />
        ))}

        {/* Days of the month */}
        {Array.from({ length: daysInMonth }, (_, i) => renderCalendarDay(i + 1))}
      </div>
    </div>
  )
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Select a date',
  dateFormat = 'dd.MM.yyyy',
  minDate,
  maxDate,
  showWeekNumbers = false,
  disabledDates = [],
  disableWeekends = false,
  label,
  required = false,
  disabled = false,
  error,
  helperText,
  className
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false)

  // Convert string to Date if needed
  const dateValue = value
    ? (typeof value === 'string' ? new Date(value) : value)
    : null

  const formatDate = (date: Date | null) => {
    if (!date) return ''

    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()

    switch (dateFormat) {
      case 'dd.MM.yyyy':
        return `${day}.${month}.${year}`
      case 'MM/dd/yyyy':
        return `${month}/${day}/${year}`
      case 'yyyy-MM-dd':
        return `${year}-${month}-${day}`
      default:
        return `${day}.${month}.${year}`
    }
  }

  const handleDateSelect = (date: Date) => {
    onChange(date)
    setIsOpen(false)
  }

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label className={labelStyles}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <PopoverPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              baseInputStyles,
              'flex items-center justify-between text-left',
              error ? inputVariants.error : inputVariants.default,
              disabled && inputVariants.disabled
            )}
          >
            <span className={cn(
              dateValue ? 'text-gray-900' : 'text-gray-500'
            )}>
              {dateValue ? formatDate(dateValue) : placeholder}
            </span>
            <Calendar className="h-4 w-4 text-gray-400" />
          </button>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className="z-50 bg-white border border-gray-200 rounded-md shadow-lg"
            sideOffset={4}
            align="start"
          >
            <Calendar
              selected={dateValue}
              onSelect={handleDateSelect}
              minDate={minDate}
              maxDate={maxDate}
              disabledDates={disabledDates}
              disableWeekends={disableWeekends}
              showWeekNumbers={showWeekNumbers}
            />
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      {/* Error Message */}
      {error && (
        <p className={errorStyles}>{error}</p>
      )}

      {/* Helper Text */}
      {helperText && !error && (
        <p className={helperTextStyles}>{helperText}</p>
      )}
    </div>
  )
}